import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { User } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { PlusIcon } from '@heroicons/react/24/outline';

const roleStyles: Record<string, string> = {
  superadmin: 'bg-sage/20 text-sage border border-sage/30',
  org_admin: 'badge-warning',
  affiliate: 'badge-success',
};

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params: { role?: string; search?: string } = {};
      if (filter) params.role = filter;
      if (search) params.search = search;

      const response = await adminApi.listUsers(params);
      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleCreateSuperadmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const response = await adminApi.createSuperadmin(newUser);
      if (response.data.success) {
        toast.success('Superadmin created successfully');
        setShowCreateModal(false);
        setNewUser({ email: '', password: '', firstName: '', lastName: '' });
        fetchUsers();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create superadmin');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-charcoal">Users</h2>
          <p className="text-charcoal/70 mt-1">Manage all users on the platform</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Superadmin
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            placeholder="Search users..."
          />
        </form>
        <div className="flex gap-2">
          {['', 'superadmin', 'org_admin', 'affiliate'].map((role) => (
            <button
              key={role}
              onClick={() => setFilter(role)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                filter === role
                  ? 'bg-sage text-white'
                  : 'bg-cream text-charcoal hover:bg-tan/30'
              )}
            >
              {role ? role.replace('_', ' ') : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-warm-white shadow-sm border border-cream overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sage"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Verified</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Last Login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-charcoal/60">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', roleStyles[user.role])}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'badge',
                        user.status === 'active' ? 'badge-success' : 'badge-danger'
                      )}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.emailVerified ? (
                        <span className="text-sage font-medium">Yes</span>
                      ) : (
                        <span className="text-charcoal/40">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {format(new Date(user.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {user.lastLoginAt
                        ? format(new Date(user.lastLoginAt), 'MMM d, yyyy')
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm">
          <div className="rounded-xl bg-warm-white shadow-xl w-full max-w-md animate-fade-in border border-cream">
            <div className="px-6 py-4 border-b border-cream flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-charcoal">Create Superadmin</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-charcoal/60 hover:text-charcoal text-2xl leading-none">
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateSuperadmin} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name *</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Last Name *</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    className="input"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="label">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="input"
                  required
                  minLength={8}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={isCreating} className="btn btn-primary flex-1">
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
